import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useChat } from "@ai-sdk/react";
import { DefaultChatTransport, type UIMessage } from "ai";
import ReactMarkdown from "react-markdown";
import { Send, Sparkles, Square, AlertCircle, Bot, User as UserIcon } from "lucide-react";
import { AdminShell } from "@/components/admin/admin-shell";
import { requireRole } from "@/lib/session";
import { useSchoolContext } from "@/lib/school-context";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/admin/assistant")({
  ssr: false,
  beforeLoad: () => requireRole("admin"),
  head: () => ({ meta: [{ title: "Admin Assistant · MAKAI" }] }),
  component: Page,
});

const QUICK_PROMPTS: { label: string; prompt: string }[] = [
  { label: "Students at risk", prompt: "Which students are currently at risk academically or by attendance? Show me the top 10 with reasons." },
  { label: "Teachers missing lesson plans", prompt: "Which teachers have not submitted lesson plans in the last 14 days?" },
  { label: "Attendance trends", prompt: "Show me attendance trends across the school over the last 8 weeks." },
  { label: "Subject performance", prompt: "How is each subject performing on average? Highlight the weakest 3 subjects." },
  { label: "Fees summary", prompt: "Give me a fees collection summary: billed, collected, outstanding and overdue invoices." },
  { label: "Predict exam performance", prompt: "Based on current data, predict how the school is likely to perform in end-of-term exams. Explain the main drivers." },
  { label: "Summarise analytics", prompt: "Summarise this term's school analytics — enrolment, attendance, performance and finance — in one page." },
  { label: "Draft announcement", prompt: "Draft a friendly parent announcement reminding them that fees are due next Friday and term reports will be available the following week." },
];

function Page() {
  const { active, loading } = useSchoolContext();
  const schoolId = active?.school_id ?? null;

  return (
    <AdminShell
      title="Admin Assistant"
      subtitle="Ask in plain English — MAKAI pulls live data from your school to answer."
    >
      {loading ? (
        <div className="rounded-2xl border border-border/70 bg-card p-10 text-center text-sm text-muted-foreground">
          Loading school context…
        </div>
      ) : !schoolId ? (
        <div className="rounded-2xl border border-border/70 bg-card p-10 text-center">
          <AlertCircle className="mx-auto mb-2 h-6 w-6 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">Select a school from the picker above to start.</p>
        </div>
      ) : (
        <Chat key={schoolId} schoolId={schoolId} />
      )}
    </AdminShell>
  );
}

function Chat({ schoolId }: { schoolId: string }) {
  const [input, setInput] = useState("");
  const scrollerRef = useRef<HTMLDivElement | null>(null);

  const { messages, sendMessage, status, error, stop } = useChat({
    transport: new DefaultChatTransport({
      api: "/api/admin/assistant",
      prepareSendMessagesRequest: async ({ messages, body }) => {
        const { data } = await supabase.auth.getSession();
        const token = data.session?.access_token ?? "";
        return {
          body: { ...(body ?? {}), schoolId, messages },
          headers: { Authorization: `Bearer ${token}` },
        };
      },
    }),
  });

  const busy = status === "submitted" || status === "streaming";

  useEffect(() => {
    const el = scrollerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, busy]);

  const send = (text: string) => {
    const t = text.trim();
    if (!t || busy) return;
    sendMessage({ role: "user", parts: [{ type: "text", text: t }] });
    setInput("");
  };

  return (
    <div className="grid h-[calc(100vh-12rem)] grid-rows-[1fr_auto] gap-3 overflow-hidden rounded-2xl border border-border/70 bg-card shadow-card">
      <div ref={scrollerRef} className="min-h-0 overflow-y-auto px-3 py-4 sm:px-6">
        {messages.length === 0 ? (
          <EmptyState onPick={send} />
        ) : (
          <div className="mx-auto flex max-w-3xl flex-col gap-5">
            {messages.map((m) => <MessageBubble key={m.id} message={m} />)}
            {busy && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-primary" />
                MAKAI is thinking…
              </div>
            )}
            {error && (
              <div className="rounded-xl border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {error.message || "Something went wrong."}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="border-t border-border/70 bg-muted/30 p-3 sm:p-4">
        <div className="mx-auto flex max-w-3xl items-end gap-2">
          <Textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(input); }
            }}
            placeholder="Ask MAKAI about your school…"
            className="min-h-[44px] max-h-40 flex-1 resize-none bg-card"
            rows={1}
          />
          {busy ? (
            <Button type="button" variant="outline" size="icon" onClick={() => stop()} aria-label="Stop">
              <Square className="h-4 w-4" />
            </Button>
          ) : (
            <Button type="button" size="icon" onClick={() => send(input)} disabled={!input.trim()} aria-label="Send">
              <Send className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function EmptyState({ onPick }: { onPick: (p: string) => void }) {
  return (
    <div className="mx-auto flex max-w-3xl flex-col items-center gap-6 py-6 text-center">
      <div className="grid h-14 w-14 place-items-center rounded-2xl bg-gradient-gold text-gold-foreground shadow-card">
        <Sparkles className="h-7 w-7" />
      </div>
      <div>
        <h2 className="font-display text-2xl font-bold">MAKAI — Admin Assistant</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Ask anything about your school. MAKAI reads live enrolment, attendance, assessments and finance — and can draft communications too.
        </p>
      </div>
      <div className="grid w-full grid-cols-1 gap-2 sm:grid-cols-2">
        {QUICK_PROMPTS.map((q) => (
          <button
            key={q.label}
            type="button"
            onClick={() => onPick(q.prompt)}
            className="rounded-xl border border-border/70 bg-card p-3 text-left text-sm transition-colors hover:bg-muted"
          >
            <div className="font-semibold">{q.label}</div>
            <div className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{q.prompt}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

function MessageBubble({ message }: { message: UIMessage }) {
  const isUser = message.role === "user";
  const text = message.parts
    .map((p) => (p.type === "text" ? p.text : ""))
    .join("")
    .trim();

  const toolParts = message.parts.filter((p) => p.type?.toString().startsWith("tool-")) as Array<{
    type: string; state?: string;
  }>;

  return (
    <div className={cn("flex gap-3", isUser ? "flex-row-reverse" : "")}>
      <div className={cn(
        "grid h-8 w-8 shrink-0 place-items-center rounded-full text-xs font-bold",
        isUser ? "bg-primary text-primary-foreground" : "bg-gradient-gold text-gold-foreground",
      )}>
        {isUser ? <UserIcon className="h-4 w-4" /> : <Bot className="h-4 w-4" />}
      </div>
      <div className={cn(
        "min-w-0 max-w-[85%] rounded-2xl px-4 py-2.5 text-sm shadow-sm",
        isUser ? "bg-primary text-primary-foreground" : "bg-muted",
      )}>
        {!isUser && toolParts.length > 0 && (
          <div className="mb-2 flex flex-wrap gap-1.5">
            {toolParts.map((p, i) => (
              <span key={i} className="inline-flex items-center gap-1 rounded-md bg-card/70 px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                <Sparkles className="h-3 w-3" />
                {prettyToolName(p.type)}
                {p.state && p.state !== "output-available" && <span className="opacity-70">· {p.state}</span>}
              </span>
            ))}
          </div>
        )}
        {isUser ? (
          <div className="whitespace-pre-wrap">{text}</div>
        ) : (
          <div className="prose prose-sm max-w-none dark:prose-invert prose-headings:font-display prose-p:my-2 prose-table:my-2 prose-li:my-0.5">
            <ReactMarkdown>{text || (toolParts.length ? "_Gathering data…_" : "")}</ReactMarkdown>
          </div>
        )}
      </div>
    </div>
  );
}

function prettyToolName(t: string) {
  const raw = t.replace(/^tool-/, "");
  return raw
    .replace(/^get|^list/, "")
    .replace(/([A-Z])/g, " $1")
    .trim()
    .replace(/^./, (c) => c.toUpperCase());
}
